<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import InputGroup from '@/components/Auths/InputGroup.vue'
import ButtonLoading from '@/components/Buttons/ButtonLoading.vue'
import { ValidationError } from 'yup'
import { useErrorStore } from '@/store/error'
import yup from '@/plugins/yup'
import OutlineButton from '@/components/Globals/Buttons/OutlineButton.vue'
import { useAlarmProfileStore } from '@/store/alarmProfile'
import { IPeakHourAlarmSensor } from '@/interfaces/entities/alarm_profile'
import {
  formatOutsideRegularUtcTimeToLocalHHmm,
  localTimeInputToUtcIsoInstantForApi,
} from '@/utils/date/date-time'

const { t } = useI18n()
const profileStore = useAlarmProfileStore()
const isLoading = ref(false)

const emit = defineEmits(['cancel'])

const props = defineProps<{
  currentProfile: IPeakHourAlarmSensor | null,
  formType: 'create' | 'edit',
}>()

const currentSettings = props.currentProfile?.settings

const inputs = ref([
  {
    type: 'text',
    label: 'Nome',
    placeholder: 'Informe o nome do perfil',
    value: props.formType === 'edit' ? props.currentProfile?.name ?? '' : '',
  },
  {
    type: 'text',
    label: 'Corrente mínima (A)',
    placeholder: 'alarms.current_message',
    value: props.formType === 'edit' ? currentSettings?.peak_hour_current_threshold ?? '' : '',
  },
  {
    type: 'text',
    label: 'Porcentagem mínima de interrupção (%)',
    placeholder: 'alarms.peak_hour.peak_hour_min_percentage_interrupt',
    value: props.formType === 'edit' ? currentSettings?.peak_hour_min_percentage_interrupt ?? '' : '',
  },
  {
    type: 'time',
    label: 'Início do horário de ponta (HH:mm)',
    placeholder: 'alarms.peak_hour.peak_hour_start_time',
    value: props.formType === 'edit' ? (currentSettings?.peak_hour_start_time ? formatOutsideRegularUtcTimeToLocalHHmm(currentSettings.peak_hour_start_time) : '21:00') : '21:00',
  },
  {
    type: 'text',
    label: 'Duração do horário de ponta (min)',
    placeholder: 'alarms.peak_hour.peak_hour_duration_minutes',
    value: props.formType === 'edit' ? currentSettings?.peak_hour_duration_minutes ?? '' : '',
  },
])

const peakHourSchema = yup.object({
  name: yup
  .string()
  .required('O campo nome é obrigatório'),
  peak_hour_current_threshold: yup.number().required('Corrente mínima é obrigatória').min(1, 'A corrente mínima deve ser maior que zero'),
  peak_hour_min_percentage_interrupt: yup.number().required('Porcentagem mínima de interrupção é obrigatória').min(1, 'A porcentagem mínima deve ser maior que zero').max(100, 'A porcentagem mínima deve ser no máximo 100'),
  peak_hour_start_time: yup
    .string()
    .required()
    .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9])?$/, 'Formato do horário de início deve ser HH:mm'),

  peak_hour_duration_minutes: yup.number().required('Duração do horário de ponta é obrigatória').min(30, 'Mínimo de 30 minutos').max(360, 'Máximo de 6 horas'),
})



async function handleSubmit() {
  const values: IPeakHourAlarmSensor = {
    name: String(inputs.value[0].value),
    peak_hour_current_threshold: inputs.value[1].value,
    peak_hour_min_percentage_interrupt: inputs.value[2].value,
    peak_hour_start_time: inputs.value[3].value,
    peak_hour_duration_minutes: inputs.value[4].value,
  }

  try {
    await peakHourSchema.validate(values, { abortEarly: false })
    isLoading.value = true
    values.peak_hour_start_time = localTimeInputToUtcIsoInstantForApi(String(values.peak_hour_start_time))
    if(props.formType === 'create'){
      await profileStore.createPeakHourAlarmProfile(values)
    } else if(props.formType === 'edit' && props.currentProfile){
      await profileStore.editPeakHourAlarmProfile(props.currentProfile.id, values)
    }
    isLoading.value = false
    emit('cancel')
  } catch (err: any) {
    if (err instanceof ValidationError) {
      useErrorStore().clearErrors();
      useErrorStore().setErrors('Erro ao atualizar o alarme: ',err.errors)
    }
  }
}
</script>

<template>
  <div class="border border-stroke bg-gray-2 p-5 shadow-sm">
    <div class="flex flex-wrap gap-4">
      <span v-if="props.formType ==='create'" class="w-full text-base font-medium text-gray-500 mb-1">
        {{ "Criar perfil de Horário de Pico" }}
      </span>
      <div v-if="props.formType === 'edit'" class="w-full flex justify-items-center">
        <span class="w-full text-base font-medium text-gray-500 mb-1">
          {{ "Editar perfil: " + (props.currentProfile?.name || '') }}
        </span>
      </div>
      <div class="w-full border-t border-stroke pt-1"></div>
      <template v-for="(input, index) in inputs" :key="index">
        <div class="min-w-[200px] w-full">
          <InputGroup
            v-model="input.value"
            :type="input.type ?? 'text'"
            :label="t(input.label)"
            :placeholder="t(input.placeholder)"
          />
        </div>
      </template>

      <div class="flex w-full justify-end gap-2">
        <OutlineButton 
          class="!h-auto w-32 !py-2 !text-sm !px-2 !border-stroke !bg-white !shadow-sm hover:!bg-gray-2 !flex !items-center !justify-center whitespace-nowrap"
          @click="emit('cancel')"
          label="Cancelar" />
        <ButtonLoading 
          class="!h-auto w-32 !py-2 !text-sm !px-2 !text-white !border-blue-600 !bg-blue-600 !shadow-sm hover:!bg-blue-700 !flex !items-center !justify-center whitespace-nowrap"
          :isLoading="isLoading"
          @submit="handleSubmit"
          label="Salvar" />
      </div>
    </div>
  </div>
</template>

<style scoped>
  :deep(label) {
    color: #64748b;
    font-size: 0.875rem;
  }
  :deep(input) {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
</style>
